import React, { useState } from 'react'
import { PayPalCheckout, paypalCheckout } from 'braintree-web'
import { AuthorizationResponse } from 'paypal-checkout-components'
import { useBraintree } from './use-braintree';
import { useBasketStore, useGlobalStore, useOrderStore } from '../store/hooks/useStore';
import { ButtonsRendererOptions, PaymentMethodSetupData, ButtonLabelOption, ButtonShapeOption, ButtonColorOption, FlowType, Intent } from '../types/payments';
import { CHECKOUT_ERROR_MESSAGES, PAYMENT_METHOD_IDS } from '../utils/constants';
import { useBasketReview } from './use-basket-review';

export function useBraintreePaypal() {
  const {
    currency,
  } = useGlobalStore()
  const { createBraintreeClientInstance } = useBraintree()
  const orderStore = useOrderStore()
  const { shippingAddress } = useBasketStore()
  const {reviewBasket} = useBasketReview()
  const [hasPaypalButtonRendered, setHasPaypalButtonRendered] = useState<boolean>(false)

  const initPaypalButtons = async (data: PaymentMethodSetupData) => {
    if (shippingAddress && data.billingAddress) {
      await createBraintreePaypal(data)
    }
  }

  const createBraintreePaypalInstance = async () => {
      const { btClientInstance } = await createBraintreeClientInstance()

      const instance = await paypalCheckout.create({
        client: btClientInstance,
      })

    return instance
  }

  const getPaypalConfig = async (ppData: PaymentMethodSetupData, instance: PayPalCheckout) => {
    const { submitOrderProcess } = ppData
    const paypalButtonConfig: ButtonsRendererOptions = ({
      fundingSource: 'paypal',
      style: {
        label: ButtonLabelOption.Pay,
        height: 44,
        shape: ButtonShapeOption.Rect,
        color: ButtonColorOption.Blue
      },
      onClick: (_data, actions) => {
        if (ppData.isBillingAddressValid && !ppData.isBillingAddressValid()) {
          orderStore.setPaymentErrorMessage(CHECKOUT_ERROR_MESSAGES.BILLING_ADDRESS_INVALID)
          return actions.reject()
        }
        return actions.resolve()
      },
      createOrder: async () => {
        const isValid = await reviewBasket()
        if(!isValid) return ''
        return instance.createPayment({
          flow: FlowType.Checkout,
          amount: ppData.amountToPay || orderStore.amountToPay,
          currency: currency,
          locale: 'en_GB',
          intent: Intent.Authorize,
          enableShippingAddress: true,
          shippingAddressEditable: false,
          shippingAddressOverride: {
            recipientName: shippingAddress?.fullName,
            line1: shippingAddress?.address1 || '',
            line2: shippingAddress?.address2 || '',
            city: shippingAddress?.city || '',
            countryCode: shippingAddress?.countryCode?.toUpperCase() || 'GB',
            postalCode: shippingAddress?.postalCode || '',
            state: shippingAddress?.stateCode || '',
            phone: shippingAddress?.phone || '',
          },
        });
      },
      onApprove: (data) => {
        return new Promise<AuthorizationResponse>((resolve, reject) => {
          instance.tokenizePayment(data, async (tokenizeErr, payload) => {
            try {
              const billingAddress = ppData.getBillingAddress?.() ?? ppData.billingAddress
              if (payload.nonce && billingAddress) {
                await submitOrderProcess({ token: payload.nonce, cardType: payload.type, billingAddress, paymentMethodId: PAYMENT_METHOD_IDS.PAYPAL, accountEmail: payload.details.email });
                resolve(payload);
              }
            } catch (error) {
              console.error(error);
              orderStore.setPaymentErrorMessage(CHECKOUT_ERROR_MESSAGES.ORDER_PROCESSING_ERROR)
              reject(error);
              return;
            }
            if (tokenizeErr) {
              console.error('Error tokenizing payment:', tokenizeErr);
              orderStore.setPaymentErrorMessage(CHECKOUT_ERROR_MESSAGES.TOKENIZE_ERROR)
              reject(tokenizeErr);
              return;
            }
            resolve(payload);
          });
        });
      },
      onCancel: (data) => {
        console.log('PayPal payment cancelled', JSON.stringify(data, null, 2));
      },
      onError: (err) => {
        console.error('PayPal error', err);
        orderStore.setPaymentErrorMessage(CHECKOUT_ERROR_MESSAGES.PAYPAL_GENERAL_ERROR)
      },
    })

    return paypalButtonConfig
  }

  const renderPayPalButtons = async (ppData: PaymentMethodSetupData, instance: PayPalCheckout) => {

    const paypalButtonConfig = await getPaypalConfig(ppData, instance)

    const containerIds = ['PayPal-container-1', 'PayPal-container-2'];
    containerIds.forEach((containerId) => {
      const containers = document.querySelectorAll(`[id="${containerId}"]`);

      containers.forEach((container) => {
        // @ts-ignore Paypal module has badly defined type here
        window.paypal.Buttons(paypalButtonConfig).render(container).catch((error) => {
          console.error('Error creating PayPal button: ' + error)
          orderStore.setPaymentErrorMessage(CHECKOUT_ERROR_MESSAGES.PAYPAL_BUTTON_ERROR);
          throw new Error(error);
        });
      });
    });
    setHasPaypalButtonRendered(true)
  }

  const createBraintreePaypal = async (ppData: PaymentMethodSetupData) => {
    try {
      if (!hasPaypalButtonRendered) {
        const instance = await createBraintreePaypalInstance()

        if (!instance) {
          throw Error('Error connecting to braintree')
        }

        await instance.loadPayPalSDK({
          currency: 'GBP',
          intent: Intent.Authorize,
          vault: false,
          locale: 'en_GB',
          'enable-funding': 'card,credit,paylater'
        }, async (loadPayPalSDKErr) => {
          if (loadPayPalSDKErr) {
            console.error('Error loading PayPal SDK:', loadPayPalSDKErr);
            return;
          }
          renderPayPalButtons(ppData, instance)
        });
      }
    } catch (error) {
      console.error(error)
    }
  }

  return {
    initPaypalButtons,
    hasPaypalButtonRendered
  }
}
